import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  AlertTriangle,
  CreditCard,
  CheckCircle,
  Clock,
  ArrowRight,
  Phone,
  Mail,
} from "lucide-react";

const SUBSCRIPTION_PLANS = [
  {
    id: "base",
    name: "Plan Sin Facturación",
    price: 2500,
    description: "Gestión empresarial completa",
    features: [
      "Gestión de clientes y proveedores",
      "Cotizaciones ilimitadas",
      "Control de proyectos",
      "Órdenes de compra",
      "Reportes básicos",
    ],
  },
  {
    id: "with_billing",
    name: "Plan Con Facturación",
    price: 3000,
    description: "Todo incluido + CFDI 4.0",
    features: [
      "Todo del plan sin facturación",
      "Facturación Electrónica CFDI 4.0",
      "Timbrado ilimitado",
      "Notas de crédito",
      "Complementos de pago",
    ],
    recommended: true,
  },
];

const SubscriptionExpired = () => {
  const { company, logout } = useAuth();
  const { slug } = useParams();
  const navigate = useNavigate();

  const handleSelectPlan = (planId) => {
    navigate(`/empresa/${slug}/subscription?plan=${planId}`);
  };

  const handleLogout = () => {
    const savedSlug = logout();
    navigate(`/empresa/${savedSlug || slug}/login`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Warning Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-4">
            <AlertTriangle className="h-10 w-10 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            Tu período de prueba ha terminado
          </h1>
          <p className="text-slate-600 max-w-md mx-auto">
            Para continuar usando CIA Servicios®, selecciona un plan de suscripción
          </p>
          {company && (
            <Badge variant="outline" className="mt-4">
              <Clock className="h-3 w-3 mr-1" />
              {company.business_name}
            </Badge>
          )}
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {SUBSCRIPTION_PLANS.map((plan) => (
            <Card
              key={plan.id}
              className={`relative cursor-pointer transition-all hover:shadow-lg ${
                plan.recommended ? "border-2 border-blue-500" : ""
              }`}
              onClick={() => handleSelectPlan(plan.id)}
            >
              {plan.recommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600">
                  Más Popular
                </Badge>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-blue-600">
                    ${plan.price.toLocaleString()}
                  </span>
                  <span className="text-slate-500">/mes</span>
                </div>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full ${
                    plan.recommended
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-slate-800 hover:bg-slate-900"
                  }`}
                >
                  Seleccionar Plan
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Info */}
        <Card className="bg-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg">¿Necesitas ayuda?</h3>
                <p className="text-slate-300 text-sm">
                  Contáctanos para resolver tus dudas sobre los planes
                </p>
              </div>
              <div className="flex gap-4">
                <a
                  href="tel:+523312345678"
                  className="flex items-center gap-2 text-sm hover:text-blue-300"
                >
                  <Phone className="h-4 w-4" />
                  33 1234 5678
                </a>
                <a
                  href="mailto:soporte@ciasistem.com"
                  className="flex items-center gap-2 text-sm hover:text-blue-300"
                >
                  <Mail className="h-4 w-4" />
                  soporte@ciasistem.com
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logout Link */}
        <div className="text-center mt-6">
          <button
            onClick={handleLogout}
            className="text-slate-500 hover:text-slate-700 text-sm underline"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionExpired;
